import { persistentKeyboardKeepFocused } from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { GraphemeMnemonicsFlashcardSurface } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels";
import { WordSentenceBackView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.word-sentence-back";
import {
  LearnFlashcardSurfaceReadyMarker,
  useLearnCardSurface,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import {
  DrillPageContext,
  GraphemeDrillContext,
  WordDrillContext,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { useEffect, type ReactNode } from "react";
import { VoiceStudyFlashcardBack } from "#voice-study-card-back";

/** Effects that belong to the learner's LIVE back only. */
function KeepReviewKeyboardFocused() {
  // The review keyboard session stays bound to the (now hidden) front's field so rating shortcut
  // keys keep working and the next card inherits focus.
  useEffect(() => {
    persistentKeyboardKeepFocused();
  }, []);
  return null;
}

/** The answer side as a deck card face, shared by both branches. */
function DrillFlashcardBackLayout({
  activeEffects,
  children,
}: {
  activeEffects: ReactNode;
  children: ReactNode;
}) {
  const { isActive } = useLearnCardSurface();
  return (
    <>
      {isActive ? (
        <>
          <KeepReviewKeyboardFocused />
          {activeEffects}
        </>
      ) : null}
      {children}
      <LearnFlashcardSurfaceReadyMarker />
    </>
  );
}

/**
 * A grapheme card's answer: its mnemonics, opening on the reading being drilled for a
 * pronunciation card.
 */
export function GraphemeDrillFlashcardBack() {
  const [character] = useContextSelectors(DrillPageContext, (c) => c.character);
  const [drillType] = useContextSelectors(GraphemeDrillContext, (c) => c.drillType);
  return (
    <DrillFlashcardBackLayout activeEffects={<VoiceStudyFlashcardBack />}>
      <GraphemeMnemonicsFlashcardSurface
        initialPinyin={drillType.kind === "pronunciation" ? drillType.pinyin : undefined}
        rawGrapheme={character}
      />
    </DrillFlashcardBackLayout>
  );
}

/** A word card's answer: the sentence it was reviewed in plus per-character tiles. */
export function WordDrillFlashcardBack() {
  const [character] = useContextSelectors(DrillPageContext, (c) => c.character);
  const [characterBacks, sentence] = useContextSelectors(
    WordDrillContext,
    (c) => c.characterBacks,
    (c) => c.sentence,
  );
  return (
    // A word card publishes no voice-study back turn: the voice back speaks a grapheme's
    // mnemonic, which a word does not have.
    <DrillFlashcardBackLayout activeEffects={null}>
      <WordSentenceBackView
        characterBacks={characterBacks}
        sentence={sentence.sentence}
        translation={sentence.translation}
        word={character}
      />
    </DrillFlashcardBackLayout>
  );
}
